import { motion, useMotionValue, useSpring, useTransform, type Variants } from "framer-motion";
import type { ReactNode, PointerEvent } from "react";

const ease = [0.22, 1, 0.36, 1] as const;

/** Title revealed line by line behind a mask. */
export const MaskLines = ({ lines, className = "", as = "h2", delay = 0 }: { lines: ReactNode[]; className?: string; as?: "h1" | "h2"; delay?: number }) => {
  const Tag = motion[as];
  return (
    <Tag className={className} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-10% 0px" }}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em]">
          <motion.span
            className="block"
            variants={{ hidden: { y: "110%" }, show: { y: "0%", transition: { duration: 0.8, ease, delay: delay + i * 0.1 } } }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

export const stagger: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
export const rise: Variants = {
  hidden: { opacity: 0, y: 40, rotateX: 12 },
  show: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.7, ease } },
};

export const Stagger = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <motion.div className={className} style={{ perspective: 1200 }} variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-8% 0px" }}>
    {children}
  </motion.div>
);

const canHover = () => typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/** 3D tilt card following the pointer with a light glare (desktop); press feedback on touch. */
export const Card = ({ children, className = "" }: { children: ReactNode; className?: string }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 200, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-10, 10]), { stiffness: 200, damping: 18 });
  const gx = useTransform(mx, (v) => `${v * 100}%`);
  const gy = useTransform(my, (v) => `${v * 100}%`);
  const glare = useTransform([gx, gy], ([x, y]) => `radial-gradient(circle at ${x} ${y}, hsl(0 0% 100% / 0.45), transparent 55%)`);

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!canHover()) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      variants={rise}
      onPointerMove={onMove}
      onPointerLeave={reset}
      whileTap={{ scale: 0.97 }}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
      className={`group/card relative ${className}`}
    >
      {children}
      <motion.div aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 group-hover/card:opacity-100 transition-opacity duration-300" style={{ background: glare }} />
    </motion.div>
  );
};

/** Floating 3D illustration inside a card (pops out in depth). */
export const Obj = ({ src, alt, className = "", delay = 0 }: { src: string; alt: string; className?: string; delay?: number }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    width={480}
    height={480}
    className={`tile-bob select-none pointer-events-none drop-shadow-[0_18px_22px_hsl(240_10%_6%/0.22)] ${className}`}
    style={{ animationDelay: `${delay}s`, transform: "translateZ(60px)" }}
    draggable={false}
  />
);
